/**
 * ChatContext - チャットの状態とストリーミングをアプリ全体で保持する
 *
 * ChatPage はルートコンポーネントなので、/chat から別ページへ遷移すると unmount され、
 * その中で走っていたストリーミングの画面更新が止まっていた (= 課金は続くのに応答が
 * 見えない無駄)。
 *
 * 状態とストリーミングロジックをこの Provider に持ち上げ、ルーターの上位に常駐させる
 * ことで、ページを移動してもストリームが生き続け、戻れば続きが見られる。
 */
import React, {
  createContext, useContext, useState, useRef, useCallback, useEffect,
} from 'react';
import { useLanguage } from './LanguageContext';
import { useAuth } from './AuthContext';
import aiService from '../services/aiService';

const STORAGE_KEY = 'struct-code-conversations';

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

const loadConversations = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const saveConversations = (conversations) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
  } catch {
    // ignore quota errors
  }
};

const truncateTitle = (text) => (text.length > 30 ? text.substring(0, 30) + '...' : text);

/**
 * サーバーから取得した会話一覧を、ローカル (localStorage 由来) の会話配列に merge する。
 * ローカルに無い会話だけ追加する (本文は未取得 = _needsFetch、開いた時に取得)。
 * 既存のローカル会話は本文を持っているので、そのまま優先して残す。
 * 結果は created_at の新しい順。
 */
const mergeServerConversations = (local, serverList) => {
  const localIds = new Set(local.map((c) => c.id));
  const additions = serverList
    .filter((s) => s && s.id && !localIds.has(s.id))
    .map((s) => ({
      id: s.id,
      title: s.title || '(無題の会話)',
      conversation_id: '',
      messages: [],
      created_at: s.created_at || new Date().toISOString(),
      _needsFetch: (s.message_count || 0) > 0,
    }));
  if (additions.length === 0) return local;
  const merged = [...local, ...additions];
  merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return merged;
};

const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};

export function ChatProvider({ children }) {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();

  const [conversations, setConversations] = useState(loadConversations);
  const [activeConvId, setActiveConvId] = useState(null);
  // 会話ごとに独立してストリーミングできるよう、loading は「いま生成中の会話 id の配列」で持つ
  const [loadingIds, setLoadingIds] = useState([]);
  const [aiUsage, setAiUsage] = useState(null);

  // 会話 id ごとの AbortController — 1 つを止めても他の会話の生成は止まらない
  const abortMapRef = useRef(new Map());
  // 生成中の会話 id の集合 (二重送信ガード用、同期的に参照する)
  const loadingIdsRef = useRef(new Set());
  // ストリーミング中に最新値を参照するための refs (クロージャの陳腐化を防ぐ)
  const convsRef = useRef(conversations);
  const activeConvIdRef = useRef(activeConvId);
  // サーバーから本文取得中の会話 id (二重 fetch 防止)
  const fetchingRef = useRef(new Set());

  useEffect(() => { convsRef.current = conversations; }, [conversations]);
  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);

  // ある会話が生成中かどうか
  const isConvLoading = useCallback((id) => loadingIds.includes(id), [loadingIds]);

  // 会話を localStorage に永続化
  useEffect(() => { saveConversations(conversations); }, [conversations]);

  // AI 利用回数 (認証時のみ)
  useEffect(() => {
    if (isAuthenticated) {
      aiService.getUsage().then(setAiUsage).catch(() => {});
    }
  }, [isAuthenticated]);

  // 認証ユーザーは、サーバーに保存された会話履歴を読み込んで merge する。
  // localStorage は端末ローカルなので、これで別端末・localStorage 消失でも履歴が残る。
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    aiService.listConversations()
      .then((d) => {
        if (cancelled) return;
        const serverList = Array.isArray(d?.conversations) ? d.conversations : [];
        if (serverList.length === 0) return;
        setConversations((prev) => mergeServerConversations(prev, serverList));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // 履歴一覧から開いた会話 (サーバー由来で本文未取得) を、開いた時に取得する
  useEffect(() => {
    const id = activeConvId;
    if (!id) return;
    const conv = convsRef.current.find((c) => c.id === id);
    if (!conv || !conv._needsFetch || fetchingRef.current.has(id)) return;
    fetchingRef.current.add(id);
    aiService.getConversation(id)
      .then((d) => {
        const msgs = Array.isArray(d?.messages) ? d.messages : [];
        setConversations((prev) => prev.map((c) => (
          c.id === id
            ? { ...c, messages: msgs, _needsFetch: false }
            : c
        )));
      })
      .catch(() => {})
      .finally(() => { fetchingRef.current.delete(id); });
  }, [activeConvId]);

  const refreshUsage = useCallback(() => {
    aiService.getUsage().then(setAiUsage).catch(() => {});
  }, []);

  /**
   * メッセージを送信してストリーミング応答を受け取る。
   * 会話ごとに独立して動くため、別の会話が生成中でも並行して送信できる。
   * この関数は Provider 内で完結するため、ChatPage が unmount しても走り続ける。
   * @param {string} messageText
   * @param {{structCode?: string|null, forceNew?: boolean}} opts
   *   forceNew: true なら activeConv に追記せず必ず新規会話を作る
   */
  const sendMessage = useCallback(async (messageText, opts = {}) => {
    const { structCode = null, forceNew = false } = opts;
    const text = (messageText || '').trim();
    if (!text) return;

    // forceNew: 結果ページ等からの開始時は、既存の activeConv に紛れ込ませない。
    // ChatProvider は遷移後も状態を保つため activeConvId が前回の会話を指したままになり、
    // 放置すると無関係な会話に追記されてしまう。
    let convId = forceNew ? null : activeConvIdRef.current;
    let conv = forceNew ? null : (convsRef.current.find((c) => c.id === convId) || null);

    // この会話が既に生成中なら二重送信しない (別の会話は対象外 = 並行送信は許可)
    if (conv && loadingIdsRef.current.has(conv.id)) return;

    // 会話が無ければ新規作成
    if (!conv) {
      const newConv = {
        id: generateId(),
        title: truncateTitle(text),
        conversation_id: '',
        messages: [],
        created_at: new Date().toISOString(),
      };
      conv = newConv;
      convId = newConv.id;
      setConversations((prev) => [newConv, ...prev]);
      setActiveConvId(newConv.id);
    }

    // ユーザーメッセージを追加
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setConversations((prev) => prev.map((c) => {
      if (c.id !== convId) return c;
      const isFirst = c.messages.length === 0;
      return {
        ...c,
        title: isFirst ? truncateTitle(text) : c.title,
        messages: [...c.messages, userMsg],
      };
    }));

    // この会話を生成中としてマーク (他の会話の loading 状態には影響しない)
    loadingIdsRef.current.add(convId);
    setLoadingIds((prev) => (prev.includes(convId) ? prev : [...prev, convId]));

    // ストリームバッファと AbortController は会話ごとに独立 (この呼び出しに閉じる)
    let streamText = '';
    const controller = new AbortController();
    abortMapRef.current.set(convId, controller);

    try {
      const historyMessages = [
        ...conv.messages.map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: text },
      ];

      const response = await aiService.streamChatV3({
        messages: historyMessages,
        context: {
          type_code: structCode || null,
        },
        conversationId: convId,
        signal: controller.signal,
      });

      if (!response.ok) {
        const err = new Error(`API error: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      let hasAssistant = false;
      for await (const evt of aiService.iterateSSE(response)) {
        if (evt.type === 'delta' && evt.text) {
          streamText += evt.text;
          const content = streamText;
          setConversations((prev) => prev.map((c) => {
            if (c.id !== convId) return c;
            const msgs = [...c.messages];
            const last = msgs[msgs.length - 1];
            if (last && last.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, content };
            } else {
              msgs.push({ role: 'assistant', content, timestamp: new Date().toISOString() });
            }
            return { ...c, messages: msgs };
          }));
          hasAssistant = true;
        } else if (evt.type === 'error') {
          throw new Error(evt.message || 'chat stream error');
        }
      }

      if (!hasAssistant) {
        setConversations((prev) => prev.map((c) => (c.id !== convId ? c : {
          ...c,
          messages: [...c.messages, {
            role: 'assistant',
            content: t('応答を取得できませんでした。', 'Could not get a response.'),
            timestamp: new Date().toISOString(),
          }],
        })));
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const isRateLimit = err.status === 429;
        const errorMsg = isRateLimit
          ? t('本日のAIチャット回数上限に達しました。Proプランにアップグレードすると回数が増えます。', 'Daily AI chat limit reached. Upgrade to Pro for more.')
          : t('エラーが発生しました。もう一度お試しください。', 'An error occurred. Please try again.');
        setConversations((prev) => prev.map((c) => (c.id !== convId ? c : {
          ...c,
          messages: [...c.messages, {
            role: 'assistant',
            content: errorMsg,
            timestamp: new Date().toISOString(),
          }],
        })));
      }
    } finally {
      loadingIdsRef.current.delete(convId);
      setLoadingIds((prev) => prev.filter((id) => id !== convId));
      abortMapRef.current.delete(convId);
      refreshUsage();
    }
  }, [t, refreshUsage]);

  // 指定した会話の生成を停止 (SSE 切断 → Anthropic 側の生成も止まる)。他の会話は止めない
  const stopGeneration = useCallback((convId) => {
    const id = convId || activeConvIdRef.current;
    if (!id) return;
    const controller = abortMapRef.current.get(id);
    if (controller) {
      controller.abort();
      abortMapRef.current.delete(id);
    }
    loadingIdsRef.current.delete(id);
    setLoadingIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const deleteConversation = useCallback((id) => {
    // サーバー側の履歴も削除する (認証時のみ成功。失敗は UI に影響させない)
    aiService.deleteConversation(id);
    // 生成中の会話を消すときはストリームも止める (課金を無駄にしない)
    const controller = abortMapRef.current.get(id);
    if (controller) {
      controller.abort();
      abortMapRef.current.delete(id);
    }
    loadingIdsRef.current.delete(id);
    setLoadingIds((prev) => prev.filter((x) => x !== id));
    setConversations((prev) => prev.filter((c) => c.id !== id));
    setActiveConvId((prev) => (prev === id ? null : prev));
  }, []);

  const value = {
    conversations,
    activeConvId,
    setActiveConvId,
    loadingIds,
    isConvLoading,
    aiUsage,
    sendMessage,
    stopGeneration,
    deleteConversation,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export default ChatContext;
